import ErrorMessage from 'components/ErrorMessage'
import FormLabel from 'components/FormLabel'
import InputField from 'components/InputField'
import { states } from 'constants/states'
import { InvestingPersonEnum } from 'interfaces'
import React, { ChangeEvent } from 'react'
import { Controller } from 'react-hook-form'
import PhoneInput from 'react-phone-input-2'
import { validatePhone, validateUSPhone } from 'utils'
import PrimaryFieldsSection from './PrimaryFieldsSection'

const Company = (props: any) => {
  return (
    <>
      <div className="py-4 text-left text-2xl font-semibold text-gray-900 md:py-12 md:pb-10 md:text-4xl">
        A few details about yourself and the company...
      </div>

      <div className="mb-4 flex text-xl">
        <div
          className={`w-1/2 border-b-2 pb-2 pr-4 pt-4 hover:cursor-pointer lg:w-auto ${
            props.showPrimary ? 'border-primary-600 font-semibold text-primary-600' : 'border-gray-200'
          }`}
          onClick={() => props.setShowPrimary(true)}
        >
          Authorized Individual
        </div>
        <div
          className={`w-1/2 border-b-2 pb-2 pt-4 hover:cursor-pointer lg:w-auto ${
            !props.showPrimary ? 'border-primary-600 font-semibold text-primary-600' : 'border-gray-200'
          }`}
          onClick={() => props.setShowPrimary(false)}
        >
          Company
        </div>
      </div>

      <PrimaryFieldsSection {...props} />

      {!props.showPrimary &&
        props.getValues('investingProfile.option') === InvestingPersonEnum.IN_BEHALF_OF_AN_ENTITY && (
          <>
            <div className="my-1 w-full">
              <FormLabel labelName={'Company Name'} />
              <InputField
                registerField={props.registerField}
                fieldId={'entityDetails.companyName'}
                required={true}
                validate={{
                  minLength: (value: string) => value.length > 1,
                }}
                placeholder={'Company ABC'}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  props.clearErrors('entityDetails.companyName')
                  props.setValue('entityDetails.companyName', e.target.value)
                }}
              />

              {props.errors.entityDetails?.companyName?.type === 'minLength' && (
                <ErrorMessage message={'Minimum of two characters.'} />
              )}
              {props.errors.entityDetails?.companyName?.type === 'validName' && (
                <ErrorMessage message={'Not a valid company name.'} />
              )}
              {props.errors.entityDetails?.companyName?.type === 'required' && (
                <ErrorMessage message={'Company name is required.'} />
              )}
            </div>

            <div className="flex flex-col items-start justify-between gap-0 md:gap-4 lg:flex-row">
              <div className="my-1 w-full">
                <FormLabel labelName={'Company Email'} />
                <InputField
                  registerField={props.registerField}
                  fieldId={'entityDetails.email'}
                  type={'email'}
                  value={props.getValues('entityDetails.email')}
                  required={true}
                  validate={{
                    validEmail: (value: string) => /[^@ \t\r\n]+@[^@ \t\r\n]+\.[^@ \t\r\n]+/.test(value),
                  }}
                  placeholder={'sample@company.com'}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    props.clearErrors('entityDetails.email')
                    props.setValue('entityDetails.email', e.target.value)
                  }}
                />
                {props.errors.entityDetails?.email?.type === 'validEmail' && (
                  <ErrorMessage message={'Not a valid email.'} />
                )}
                {props.errors.entityDetails?.email?.type === 'required' && (
                  <ErrorMessage message={'Email is required.'} />
                )}
              </div>

              <div className="my-1 w-full">
                <FormLabel labelName={'Title With Company'} />
                <InputField
                  registerField={props.registerField}
                  fieldId={'entityDetails.titleWithCompany'}
                  value={props.getValues('entityDetails.titleWithCompany')}
                  required={true}
                  validate={{
                    minLength: (value: string) => value.length > 1,
                  }}
                  placeholder={'President, Manager, etc.'}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    props.clearErrors('entityDetails.titleWithCompany')
                    props.setValue('entityDetails.titleWithCompany', e.target.value)
                  }}
                />

                {props.errors.entityDetails?.titleWithCompany?.type === 'minLength' && (
                  <ErrorMessage message={'Minimum of two characters.'} />
                )}
                {props.errors.entityDetails?.titleWithCompany?.type === 'validName' && (
                  <ErrorMessage message={'Not a valid company title.'} />
                )}
                {props.errors.entityDetails?.titleWithCompany?.type === 'required' && (
                  <ErrorMessage message={'Title with company is required.'} />
                )}
              </div>

              <div className="my-1 w-1/3">
                <FormLabel labelName={'State of Formation'} />
                {(props.getValues('entityDetails.countryOfFormation') as string).toLowerCase() === 'usa' ? (
                  <select
                    name="state"
                    {...props.registerField('entityDetails.regionOfFormation', { required: true })}
                    className="mt-1 h-[42px] w-full rounded-md border border-gray-300 bg-gray-50 px-1 py-2 text-base leading-5"
                    onChange={(event) => {
                      props.setValue('entityDetails.regionOfFormation', event.target.value)
                    }}
                  >
                    <option value="" disabled hidden>
                      Please select State
                    </option>
                    {states.map((state, index) => {
                      return (
                        <option key={index} value={state}>
                          {state}
                        </option>
                      )
                    })}
                  </select>
                ) : (
                  <InputField
                    registerField={props.registerField}
                    fieldId={'entityDetails.regionOfFormation'}
                    placeholder={'Province'}
                    required={true}
                  />
                )}
              </div>
            </div>

            <div className="flex flex-col items-start justify-between gap-0 md:gap-4 lg:flex-row">
              <div className="my-1 w-full">
                <FormLabel labelName={'Company Phone'} />
                {(props.getValues('entityDetails.countryOfFormation') as string).toLowerCase() === 'usa' ? (
                  <>
                    <InputField
                      registerField={props.registerField}
                      fieldId={'entityDetails.phoneNumber'}
                      validate={{
                        validatePhone: (value: string) => validatePhone(value.replace(/[^0-9]/gi, '')),
                        validateUSPhone: (value: string) => validateUSPhone(value.replace(/[^0-9]/gi, '')),
                      }}
                      control={props.control}
                      format={true}
                      mask="_"
                      maskedInput={'numeric-pattern'}
                      allowEmptyFormatting={true}
                      value={props.getValues('entityDetails.phoneNumber')}
                      onChange={(e: any) => {
                        props.clearErrors('entityDetails.phoneNumber')
                      }}
                    />
                  </>
                ) : (
                  <>
                    <Controller
                      control={props.control}
                      name={'entityDetails.phoneNumber'}
                      rules={{
                        required: true,
                        validate: {
                          validatePhone: (value: string) => validatePhone(value),
                        },
                      }}
                      render={({ field }) => (
                        <>
                          <div className="mt-1 hidden lg:flex">
                            <PhoneInput
                              {...field}
                              specialLabel={''}
                              country={
                                props.watch('entityDetails.countryOfFormation')
                                  ? props.watch('entityDetails.countryOfFormation').toLowerCase()
                                  : ''
                              }
                              value={props.getValues('entityDetails.phoneNumber')}
                              placeholder={'123456789'}
                              onChange={(e: any) => {
                                props.clearErrors('entityDetails.phoneNumber')
                                props.setValue('entityDetails.phoneNumber', e.floatValue)
                              }}
                            />
                          </div>

                          <div className="mt-1 flex lg:hidden">
                            <PhoneInput
                              {...field}
                              country={
                                props.watch('entityDetails.countryOfFormation')
                                  ? props.watch('entityDetails.countryOfFormation').toLowerCase()
                                  : ''
                              }
                              value={props.getValues('entityDetails.phoneNumber')}
                              inputStyle={{
                                padding: '3.25rem 2rem 3.25rem 3.25rem',
                                fontSize: '2.25rem',
                                width: '100%',
                                borderWidth: '5px',
                              }}
                              buttonStyle={{
                                width: '50px',
                              }}
                              onChange={(e: any) => {
                                props.clearErrors('entityDetails.phoneNumber')
                                props.setValue('entityDetails.phoneNumber', e.floatValue)
                              }}
                            />
                          </div>
                        </>
                      )}
                    />
                  </>
                )}

                {props.errors.entityDetails?.phoneNumber?.type === 'validatePhone' && (
                  <ErrorMessage message={'Invalid phone number.'} />
                )}
                {props.errors.entityDetails?.phoneNumber?.type === 'validateUSPhone' && (
                  <ErrorMessage message={'Invalid US phone number.'} />
                )}
                {props.errors.entityDetails?.phoneNumber?.type === 'required' && (
                  <ErrorMessage message={'Phone number is required.'} />
                )}
              </div>

              <div className="my-1 w-full">
                <FormLabel
                  labelName={`${
                    (props.getValues('entityDetails.countryOfFormation') as string).toLowerCase() === 'usa'
                      ? 'Company EIN'
                      : 'Company TIN'
                  }`}
                />

                <div>
                  {(props.getValues('entityDetails.countryOfFormation') as string).toLowerCase() === 'usa' ? (
                    <InputField
                      registerField={props.registerField}
                      fieldId={'entityDetails.ein'}
                      validate={{
                        maxLength: (value: string) => value.length === 10,
                        validateInput: (value: string) => {
                          return !value.includes('#')
                        },
                      }}
                      control={props.control}
                      maskedInput={'pattern'}
                      format={'##-#######'}
                      value={props.getValues('entityDetails.ein')}
                      mask="#"
                      maskType={'pattern'}
                      allowEmptyFormatting={true}
                      required={true}
                      onChange={(e: any) => {
                        props.clearErrors('entityDetails.ein')
                        props.setValue('entityDetails.ein', e.floatValue)
                      }}
                    />
                  ) : (
                    <InputField
                      registerField={props.registerField}
                      fieldId={'entityDetails.ein'}
                      placeholder={'12345'}
                      required={true}
                      control={props.control}
                      format=""
                      mask="_"
                      maskedInput={'numeric'}
                      value={props.getValues('entityDetails.ein')}
                    />
                  )}
                </div>
                {props.errors.entityDetails?.ein?.type === 'maxLength' ||
                  (props.errors.entityDetails?.ein?.type === 'validateInput' && (
                    <ErrorMessage message={'Must input a valid EIN.'} />
                  ))}
                {props.errors.entityDetails?.ein?.type === 'required' && <ErrorMessage message={'EIN is required.'} />}
              </div>

              <div className="my-1 w-full">
                <FormLabel labelName={'Company Type'} />
                <select
                  name="entityDetails.type"
                  {...props.registerField('entityDetails.type', { required: true })}
                  className="mt-1 h-[42px] w-full rounded-md border border-gray-300 bg-gray-50 px-1 py-2 text-base leading-5"
                  onChange={(event) => {
                    props.setValue('entityDetails.type', event.target.value)
                    props.clearErrors('entityDetails.type')
                  }}
                >
                  <option value="" disabled selected hidden>
                    Please select Company Type
                  </option>
                  {['Corporation', 'LLC', 'Other'].map((type, index) => {
                    return (
                      <option key={index} value={type} className="text-black">
                        {type}
                      </option>
                    )
                  })}
                </select>

                {props.getValues('entityDetails.type') === 'Other' ? (
                  <>
                    <InputField
                      registerField={props.registerField}
                      fieldId={'entityDetails.legalStructure'}
                      required={true}
                      validate={{
                        minLength: (value: string) => value.length > 1,
                        validName: (value: string) => /^[A-Za-z-'\s]+$/.test(value),
                      }}
                      placeholder={'Other'}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        props.clearErrors('entityDetails.legalStructure')
                        props.setValue('entityDetails.legalStructure', e.target.value)
                      }}
                    />

                    {props.errors.entityDetails?.legalStructure?.type === 'minLength' && (
                      <ErrorMessage message={'Minimum of two characters.'} />
                    )}
                    {props.errors.entityDetails?.legalStructure?.type === 'validName' && (
                      <ErrorMessage message={'Not a valid company type.'} />
                    )}
                    {props.getValues('entityDetails.type') === 'Other' &&
                      props.errors.entityDetails?.legalStructure?.type === 'required' && (
                        <ErrorMessage message={'Company type other is required.'} />
                      )}
                  </>
                ) : null}
                {props.errors.entityDetails?.type?.type === 'required' && (
                  <ErrorMessage message={'Company type is required.'} />
                )}
              </div>
            </div>

            <div className={`w-1/2 pt-4 font-bold underline hover:cursor-pointer lg:w-auto`}>
              Address of Principal Place of Business
            </div>
            <i>Must be address on file with the state where the business was formed</i>
            <div className="my-1 w-full">
              <FormLabel labelName={'Street Address 1'} />
              <InputField
                registerField={props.registerField}
                fieldId={'entityDetails.addressInfo.Street1'}
                placeholder={'123 Maple St.'}
                required={true}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  props.clearErrors('entityDetails.addressInfo.Street1')
                  props.setValue('entityDetails.addressInfo.Street1', e.target.value)
                }}
              />
              {props.errors.entityDetails?.addressInfo?.Street1?.type === 'required' && (
                <ErrorMessage message={'Street address 1 is required.'} />
              )}
            </div>

            <div className="my-1 w-full">
              <FormLabel labelName={'Street Address 2'} />
              <InputField
                registerField={props.registerField}
                required={false}
                fieldId={'entityDetails.addressInfo.Street2'}
                placeholder={'Optional'}
              />
            </div>

            <div className="flex flex-col items-start justify-between gap-0 md:gap-4 lg:flex-row">
              <div className="my-1 w-full">
                <FormLabel labelName={'Country'} />
                <select
                  name="country"
                  {...props.registerField('entityDetails.addressInfo.country', { required: true })}
                  className="mt-1 h-[42px] w-full rounded-md border border-gray-300 bg-gray-50 px-1 py-2 text-base leading-5"
                  onChange={(e) => {
                    props.setValue('entityDetails.addressInfo.country', e.target.value)
                  }}
                  defaultValue={'USA'}
                >
                  <option selected value={'USA'}>
                    United States
                  </option>
                </select>
              </div>

              <div className="my-1 w-full">
                <FormLabel labelName={'City'} />
                <InputField
                  registerField={props.registerField}
                  fieldId={'entityDetails.addressInfo.city'}
                  placeholder={'Atlanta City'}
                  required={true}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    props.clearErrors('entityDetails.addressInfo.city')
                    props.setValue('entityDetails.addressInfo.city', e.target.value.replace(/[^A-Za-z +]/gi, ''))
                  }}
                />
                {props.errors.entityDetails?.addressInfo?.city?.type === 'required' && (
                  <ErrorMessage message={'City is required.'} />
                )}
              </div>

              <div className="my-1 w-full">
                <FormLabel labelName={'State / Province'} />
                {props.getValues('entityDetails.addressInfo.country') &&
                (props.getValues('entityDetails.addressInfo.country') as string).toLowerCase() === 'usa' ? (
                  <select
                    name="state"
                    {...props.registerField('entityDetails.addressInfo.state', { required: true })}
                    className="mt-1 h-[42px] w-full rounded-md border border-gray-300 bg-gray-50 px-1 py-2 text-base leading-5"
                    onChange={(event) => {
                      props.setValue('entityDetails.addressInfo.state', event.target.value)
                      props.clearErrors('entityDetails.addressInfo.state')
                    }}
                  >
                    <option value="" disabled hidden>
                      Please select State
                    </option>
                    {states.map((state, index) => {
                      return (
                        <option key={index} value={state}>
                          {state}
                        </option>
                      )
                    })}
                  </select>
                ) : (
                  <InputField
                    registerField={props.registerField}
                    fieldId={'entityDetails.addressInfo.state'}
                    placeholder={'Province'}
                    required={true}
                    onChange={() => props.clearErrors('entityDetails.addressInfo.state')}
                  />
                )}
                {props.errors.entityDetails?.addressInfo?.state?.type === 'required' && (
                  <ErrorMessage message={'State / Province is required.'} />
                )}
              </div>

              <div className="my-2 w-full md:my-1">
                <FormLabel labelName={'ZIP / Postal'} />
                <InputField
                  registerField={props.registerField}
                  fieldId={'entityDetails.addressInfo.postalCode'}
                  placeholder={'12345'}
                  required={true}
                  control={props.control}
                  format=""
                  mask="_"
                  maskedInput={'numeric'}
                  allowEmptyFormatting={true}
                  value={props.getValues('entityDetails.addressInfo.postalCode')}
                  onChange={(e: any) => {
                    props.clearErrors('entityDetails.addressInfo.postalCode')
                    props.setValue('entityDetails.addressInfo.postalCode', e.value)
                  }}
                  validate={{
                    maxLength: (value: string) => value.length === 5 || value.length === 9,
                  }}
                />

                {props.errors.entityDetails?.addressInfo?.postalCode?.type === 'maxLength' && (
                  <ErrorMessage message={'Must input a valid ZIP/Postal Code.'} />
                )}
                {props.errors.entityDetails?.addressInfo?.postalCode?.type === 'required' && (
                  <ErrorMessage message={'ZIP/Postal Code is required.'} />
                )}
              </div>
            </div>
          </>
        )}
    </>
  )
}

export default Company
